import { useEffect, useState } from 'react';
import { useAdmin } from '../store/adminStore';
import { toast } from 'sonner';

function Settings() {
  const { settings, fetchSettings, updateSettings } = useAdmin();

  const [commissionRate, setCommissionRate] = useState('');
  const [stripeKey, setStripeKey] = useState('');
  const [privacyPolicy, setPrivacyPolicy] = useState('');
  const [termsOfService, setTermsOfService] = useState('');
  const [defaultCurrency, setDefaultCurrency] = useState('EUR');
  const [defaultCountry, setDefaultCountry] = useState('DE');
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const baseCurrency = settings?.base_currency || 'EUR';
  const supportedCurrencies = settings?.supported_currencies || ['EUR', 'USD'];

  useEffect(() => {
    fetchSettings()
      .catch((e: Error) => toast.error(e.message))
      .finally(() => setLoaded(true));
  }, [fetchSettings]);

  useEffect(() => {
    if (settings && !loaded) {
      setCommissionRate(String(settings.commission_rate ?? ''));
      setStripeKey(settings.stripe_publishable_key || '');
      setPrivacyPolicy(settings.privacy_policy || '');
      setTermsOfService(settings.terms_of_service || '');
      setDefaultCurrency(settings.default_currency || 'EUR');
      setDefaultCountry(settings.default_country || 'DE');
      setExchangeRates(settings.exchange_rates || {});
    }
  }, [settings, loaded]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const rate = parseFloat(commissionRate);
    if (Number.isNaN(rate) || rate < 0 || rate > 1) {
      toast.error('Commission rate must be between 0 and 1 (e.g. 0.25 = 25%)');
      return;
    }
    for (const [code, value] of Object.entries(exchangeRates)) {
      if (Number.isNaN(value) || value <= 0) {
        toast.error(`Exchange rate for ${code} must be a positive number`);
        return;
      }
    }
    setSaving(true);
    try {
      await updateSettings({
        commission_rate: rate,
        stripe_publishable_key: stripeKey.trim(),
        privacy_policy: privacyPolicy,
        terms_of_service: termsOfService,
        default_currency: defaultCurrency,
        default_country: defaultCountry,
        exchange_rates: exchangeRates,
      });
      toast.success('Settings saved');
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container">
      <h1 className="page-title">Settings</h1>
      <p className="page-subtitle">Platform configuration, payments, and legal content.</p>

      <form className="form" onSubmit={handleSave} style={{ maxWidth: '860px' }}>
        <div className="panel">
          <h2>Payments &amp; Commissions</h2>
          <div className="form-row">
            <label htmlFor="commission">Commission rate (0.0 – 1.0)</label>
            <input
              id="commission"
              type="number"
              min="0"
              max="1"
              step="0.01"
              value={commissionRate}
              onChange={(e) => setCommissionRate(e.target.value)}
              placeholder="0.25"
            />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
              e.g. 0.25 means the store keeps 25% of each sale.
            </span>
          </div>
          <div className="form-row">
            <label htmlFor="stripe">Stripe publishable key</label>
            <input
              id="stripe"
              type="text"
              value={stripeKey}
              onChange={(e) => setStripeKey(e.target.value)}
              placeholder="pk_live_…"
            />
          </div>
        </div>

        <div className="panel">
          <h2>Currency &amp; Tax</h2>
          <p className="page-subtitle" style={{ marginTop: -8, marginBottom: 12 }}>
            Store prices are stored in the base currency (EUR). The customer frontend converts them
            using the exchange rates below (base-to-currency, so 1 EUR = 1.086957 USD).
          </p>
          <div className="form-row">
            <label htmlFor="base-currency">Base currency (read-only)</label>
            <input id="base-currency" type="text" value={baseCurrency} disabled />
          </div>
          <div className="form-row">
            <label htmlFor="default-currency">Default display currency</label>
            <select
              id="default-currency"
              value={defaultCurrency}
              onChange={(e) => setDefaultCurrency(e.target.value)}
            >
              {supportedCurrencies.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label htmlFor="default-country">Default VAT country</label>
            <input
              id="default-country"
              type="text"
              value={defaultCountry}
              onChange={(e) => setDefaultCountry(e.target.value.toUpperCase())}
              placeholder="DE"
              maxLength={2}
            />
            <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
              ISO 3166-1 alpha-2 country code (e.g. DE, AT, US).
            </span>
          </div>
          {supportedCurrencies
            .filter((code) => code !== baseCurrency)
            .map((code) => (
              <div className="form-row" key={code}>
                <label htmlFor={`eur-${code}`}>
                  {baseCurrency} → {code} exchange rate
                </label>
                <input
                  id={`rate-${code}`}
                  type="number"
                  min="0"
                  step="0.000001"
                  value={exchangeRates[code] ?? ''}
                  onChange={(e) =>
                    setExchangeRates((prev) => ({
                      ...prev,
                      [code]: parseFloat(e.target.value),
                    }))
                  }
                  placeholder="1.086957"
                />
              </div>
            ))}
        </div>

        <div className="panel">
          <h2>Legal Pages</h2>
          <div className="form-row">
            <label htmlFor="privacy">Privacy Policy</label>
            <textarea
              id="privacy"
              value={privacyPolicy}
              onChange={(e) => setPrivacyPolicy(e.target.value)}
              placeholder="Paste your privacy policy text…"
              style={{ minHeight: '180px' }}
            />
          </div>
          <div className="form-row">
            <label htmlFor="terms">Terms of Service</label>
            <textarea
              id="terms"
              value={termsOfService}
              onChange={(e) => setTermsOfService(e.target.value)}
              placeholder="Paste your terms of service text…"
              style={{ minHeight: '180px' }}
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn btn--primary btn--lg" disabled={saving || !loaded}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Settings;
